import { useEffect, useReducer, useCallback } from 'react'
import api, { IDMTableDepartment, Order, IPageInfo } from '@api'
import { debounce } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { Toaster, IGridTableCell, IGridTablePage } from '@ui'
import { useReload, useShouldReload } from '@hooks/reloadContext'
import track from '@track'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum GridTableStatusEnum {
  IDLE = 'idle',
  LOADING = 'loading',
  PAGING = 'paging',
  SORTING = 'sorting',
  FILTERING = 'filtering',
}

type SortName = 'name' | 'employeesCount' | 'spacesCount'

interface QueryVars {
  after: string | null
  orderBy: string
  first: number
  queryString?: string
  flatten: boolean
}

interface IDepartmentsHookState {
  reqId: Date
  items: IGridTableCell<IDMTableDepartment>[]
  sort: { column: SortName; order: Order }
  after: string | null
  query: string
  first: number
  totalDepartments: number
  pageInfo: IPageInfo
  status: GridTableStatusEnum
}

export interface IDepartmentsHook extends IDepartmentsHookState {
  reload: () => void
  sortBy: (column: string) => void
  nextPage: () => void
  search: (name: string) => void
}

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  PAGE,
  NEXT_PAGE,
  SEARCH,
  LOAD_TEAMS_START,
  LOAD_TEAMS,
  PAGE_TEAMS,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: Date
}
interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: Date
}

interface LoadAction {
  type: Actions.LOAD
  reqId: Date
  payload: {
    items: IGridTableCell<IDMTableDepartment>[]
    pageInfo: IPageInfo
    totalDepartments: number
  }
}

interface SortAction {
  type: Actions.SORT
  payload: {
    column: SortName
  }
}

interface SearchAction {
  type: Actions.SEARCH
  payload: { name: string }
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface LoadTeamsStartAction {
  type: Actions.LOAD_TEAMS_START
  payload: { parentId: string; isPaging: boolean }
}

interface LoadTeamsAction {
  type: Actions.LOAD_TEAMS
  payload: {
    parentId: string
    teams: IGridTableCell<IDMTableDepartment>[]
    teamsPageInfo: IGridTablePage
    isPaging: boolean
  }
}

type Action =
  | LoadAction
  | SortAction
  | NextPageAction
  | LoadStartAction
  | LoadStartActionWithoutSkeleton
  | SearchAction
  | LoadTeamsStartAction // set loading status for single data item
  | LoadTeamsAction // set idle status for single data item, & add page stuff

function reducer(state: IDepartmentsHookState, action: Action) {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
      }

    case Actions.LOAD_TEAMS_START:
      return {
        ...state,
        items: state.items.map((departmentCell) => {
          if (departmentCell.data.id !== action.payload.parentId) return departmentCell

          return {
            ...departmentCell,
            skeletonSize: Math.min(PAGE_SIZE, departmentCell.data.totals.teams),
            status: action.payload.isPaging
              ? GridTableStatusEnum.PAGING
              : GridTableStatusEnum.LOADING,
          }
        }),
      }
    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            items:
              state.status === 'paging'
                ? [...state.items, ...action.payload.items]
                : action.payload.items,
            pageInfo: action.payload.pageInfo,
            totalDepartments: action.payload.totalDepartments,
          }
        : state
    case Actions.LOAD_TEAMS:
      return {
        ...state,
        items: state.items.map((departmentCell) => {
          if (action.payload.parentId !== departmentCell.data.id) return departmentCell
          return {
            ...departmentCell,
            status: GridTableStatusEnum.IDLE,
            children:
              action.payload.isPaging && departmentCell.children && departmentCell.children.length
                ? [...departmentCell.children, ...action.payload.teams]
                : action.payload.teams,
            page: action.payload.teamsPageInfo,
          }
        }),
      }
    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        status: GridTableStatusEnum.PAGING,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.SORT:
      return {
        ...state,
        sort: {
          order:
            action.payload.column === state.sort.column
              ? state.sort.order === Order.DESC
                ? Order.ASC
                : Order.DESC
              : Order.DESC,
          column: action.payload.column,
        },
        status: GridTableStatusEnum.SORTING,
      }

    case Actions.SEARCH:
      return {
        ...state,
        query: action.payload.name,
        after: null,
        status: GridTableStatusEnum.FILTERING,
      }

    default:
      return state
  }
}

let persistedState: IDepartmentsHookState = {
  reqId: new Date(),
  items: [],
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  after: null,
  first: PAGE_SIZE,
  query: '',
  totalDepartments: 0,
  status: GridTableStatusEnum.LOADING,
}

export default function useDepartments(): IDepartmentsHook {
  // status seems to be breaking th persisted state somehow, will update later
  const [state, dispatch] = useReducer(reducer, persistedState)
  const shouldReload = useShouldReload()
  const reload = useReload()

  const debouncedFetch = debounce((reqId: Date, vars: QueryVars) => {
    api.department
      .list(vars)
      .then(({ departments, pageInfo, counters }) => {
        const items: IGridTableCell<IDMTableDepartment>[] = departments.map((d) => ({
          data: d,
          expandToggle: `${d.totals.teams || 0} Teams`,
          onExpand: d.totals.teams ? () => fetchDepartmentTeams(d.id, false) : undefined,
        }))

        persistedState.items.forEach((departmentCell) => {
          const cellRef = items.find((dep) => dep.data.id === departmentCell.data.id)
          if (departmentCell.children?.length && cellRef) {
            // set new item skeleton size
            cellRef.skeletonSize = departmentCell.children.length
            cellRef.status = 'loading'

            const teamVariables = {
              id: departmentCell.data.id,
              first: departmentCell.children.length,
              orderBy: `${persistedState.sort.column}_${persistedState.sort.order}`,
              after: '',
            }

            api.department.team
              .list(teamVariables)
              .then(({ teams, pageInfo }) => {
                if (cellRef) {
                  cellRef.children = teams.map((team) => ({
                    data: team,
                  }))
                  cellRef.page = {
                    ...pageInfo,
                    onNextPage: () => fetchDepartmentTeams(departmentCell.data.id, true),
                  }
                }
                dispatch({
                  type: Actions.LOAD,
                  payload: {
                    items: [...items],
                    pageInfo,
                    totalDepartments: counters.total,
                  },
                  reqId,
                })
              })
              .catch((error: ApolloError) => Toaster.fail({ error }))
          }
        })

        dispatch({
          type: Actions.LOAD,
          payload: {
            items,
            pageInfo,
            totalDepartments: counters.total,
          },
          reqId,
        })
      })
      .catch((error: ApolloError) => Toaster.fail({ error }))
  }, 300)

  const fetchDepartments =
    ({ loadAll, nullAfter }: { loadAll: boolean; nullAfter: boolean }) =>
    () => {
      const reqId = new Date()
      const variables: QueryVars = {
        after: loadAll || nullAfter ? null : state.after,
        orderBy: `${state.sort.column}_${state.sort.order}`,
        first: loadAll ? state.first : PAGE_SIZE,
        queryString: state.query ? state.query : undefined,
        flatten: false,
      }

      dispatch({ type: Actions.LOAD_START, reqId })

      debouncedFetch(reqId, variables)

      return () => debouncedFetch.cancel()
    }

  const fetchDepartmentTeams = useCallback((id: string, paging: boolean) => {
    // set data.status to 'loading' or 'paging' for this cell
    dispatch({
      type: Actions.LOAD_TEAMS_START,
      payload: { parentId: id, isPaging: paging },
    })

    const index = persistedState.items.findIndex(
      ({ data }: IGridTableCell<IDMTableDepartment>) => data.id === id
    )
    const variables = {
      id,
      first: PAGE_SIZE,
      orderBy: `${persistedState.sort.column}_${persistedState.sort.order}`,
      after: paging ? persistedState.items[index]?.page?.endCursor ?? '' : '',
    }

    api.department.team.list(variables).then(({ teams, pageInfo }) => {
      dispatch({
        type: Actions.LOAD_TEAMS,
        payload: {
          parentId: id,
          teams: teams.map((team) => ({ data: team })),
          teamsPageInfo: {
            ...pageInfo,
            onNextPage: () => fetchDepartmentTeams(id, true),
          },
          isPaging: paging,
        },
      })
    })
  }, [])

  useEffect(fetchDepartments({ loadAll: true, nullAfter: true }), [
    state.sort,
    shouldReload.departments,
  ])

  //loading data without skeleton and load only +25 items
  useEffect(fetchDepartments({ loadAll: false, nullAfter: false }), [state.after])

  useEffect(fetchDepartments({ loadAll: false, nullAfter: true }), [state.query])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    reload: () => reload.departments(),
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, payload: { column: column as SortName } })
      track.departments({
        eventName: track.Event.SORT,
        props: { columnName: column },
      })
    },
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
      track.departments({
        eventName: track.Event.PAGE,
      })
    },
    search: (name: string) => dispatch({ type: Actions.SEARCH, payload: { name } }),
  }
}
